import { ref } from 'vue'
import { registerSW } from 'virtual:pwa-register'

/**
 * How often to ASK the browser whether a newer build exists.
 *
 * Nothing surfaces an update until somebody checks for one, and the browser
 * decides when to check on its own. Safari re-checks when the app comes back to
 * the foreground, which is why an update showed there; Chrome on an installed
 * Android PWA can keep the same worker for hours, so the banner never appeared
 * on a Pixel or a tablet. Asking on a timer, and every time the app is looked
 * at again, is the documented way to make it dependable.
 */
const UPDATE_CHECK_MS = 60_000

// Module scope, not per component: registration happens ONCE for the whole
// app and every caller reads the same state.
//
// This lived inside the composable's body, and the composable was called only
// from AppLayout — the authenticated shell. Anybody who had not signed in yet
// was on the LOGIN screen, which sits outside that layout, so no service
// worker was ever registered and the browser had nothing to offer to install.
// `useTheme` was moved into main.ts for exactly this reason; this is the same
// bug with a different symptom.
const offlineReady = ref(false)
const needRefresh = ref(false)
let updateServiceWorker: ((reload?: boolean) => Promise<void>) | null = null

/**
 * Registers the service worker. Safe to call more than once — only the first
 * call registers, so `main.ts` can do it at startup and a component can still
 * ask for the state without registering a second worker.
 *
 * Uses the 'prompt' strategy: a new version never reloads on its own — a
 * collector mid-route decides when to apply it, so an in-progress payment form
 * is never wiped by a surprise refresh.
 */
export const registerServiceWorker = (): void => {
  if (updateServiceWorker) {
    return
  }

  let timer: ReturnType<typeof setInterval> | undefined
  let onVisible: (() => void) | undefined

  updateServiceWorker = registerSW({
    onOfflineReady() {
      // The shell is precached — the app now loads even with no signal.
      offlineReady.value = true
    },
    onNeedRefresh() {
      // A newer build is waiting; surface it instead of forcing a reload.
      needRefresh.value = true
    },
    onRegisteredSW(_swUrl, registration) {
      if (!registration) {
        return
      }

      // Offline, update() throws; a failed check is not an event worth
      // reporting, it just means we ask again next time.
      const check = (): void => {
        if (navigator.onLine !== false) {
          void registration.update().catch(() => undefined)
        }
      }

      timer = setInterval(check, UPDATE_CHECK_MS)

      // Coming back to the app is the moment somebody is most likely to be
      // about to use it, and the cheapest moment to hand them a newer one.
      onVisible = () => {
        if (document.visibilityState === 'visible') {
          check()
        }
      }
      document.addEventListener('visibilitychange', onVisible)
      window.addEventListener('online', check)
    },
  })

  // Never torn down: the registration lives as long as the page does, and the
  // timer is what makes Chrome notice a new build at all.
  void timer
  void onVisible
}

/** Activates the waiting worker and reloads into the new version. */
const applyUpdate = async (): Promise<void> => {
  needRefresh.value = false
  await updateServiceWorker?.(true)
}

const dismiss = (): void => {
  offlineReady.value = false
  needRefresh.value = false
}

/**
 * The update state, for whatever screen shows the banner. Registration is
 * `registerServiceWorker`'s job and happens at startup, so this never depends
 * on which layout the person happens to be inside.
 */
export const usePwaUpdate = () => ({ offlineReady, needRefresh, applyUpdate, dismiss })
